import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { api } from '../lib/api'
import { setSentryUser, clearSentryUser } from '../lib/sentry'

interface AdminUser {
  id: string
  email: string
  name: string
  role: string
  // True when this admin has set a POS manager PIN (used by Phase 2 of the
  // POS work — discount/void overrides at the till). Surfaced so the
  // Profile page can show "Set PIN" vs "Change / Remove PIN". Optional
  // because legacy cached users in localStorage won't have it; the field
  // re-hydrates on next /auth/me round-trip.
  pos_pin_set?: boolean
}

interface AuthContextValue {
  user: AdminUser | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  updateProfile: (name: string) => void
  // Re-fetches /auth/me and updates the cached user — used after privileged
  // self-service edits (e.g. setting / clearing the POS manager PIN) where
  // the UI needs the fresh `pos_pin_set` value without a full reload.
  refreshUser: () => Promise<void>
  isLoading: boolean
  can: (roles: string[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(() => {
    const stored = localStorage.getItem('admin_user')
    return stored ? JSON.parse(stored) : null
  })
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('admin_token'))
  const [isLoading, setIsLoading] = useState(!!localStorage.getItem('admin_token'))

  useEffect(() => {
    if (!token) { setIsLoading(false); return }
    api.get('/auth/me')
      .then((res) => {
        setUser(res.data.user)
        setSentryUser(res.data.user.id, res.data.user.role)
      })
      .catch(() => { setToken(null); setUser(null); localStorage.removeItem('admin_token'); localStorage.removeItem('admin_user'); clearSentryUser() })
      .finally(() => setIsLoading(false))
  }, [token])

  async function login(email: string, password: string) {
    const res = await api.post('/auth/login', { email, password })
    const { token: t, user: u } = res.data
    localStorage.setItem('admin_token', t)
    localStorage.setItem('admin_user', JSON.stringify(u))
    setToken(t)
    setUser(u)
    setSentryUser(u.id, u.role)
  }

  function logout() {
    localStorage.removeItem('admin_token')
    localStorage.removeItem('admin_user')
    setToken(null)
    setUser(null)
    clearSentryUser()
  }

  function updateProfile(name: string) {
    setUser(prev => {
      if (!prev) return prev
      const updated = { ...prev, name }
      localStorage.setItem('admin_user', JSON.stringify(updated))
      return updated
    })
  }

  async function refreshUser() {
    const res = await api.get('/auth/me')
    setUser(res.data.user)
    localStorage.setItem('admin_user', JSON.stringify(res.data.user))
  }

  function can(roles: string[]) {
    return !!user && roles.includes(user.role)
  }

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateProfile, refreshUser, isLoading, can }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
